import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { useAppContext } from 'context/AppContext';
import { makeStyles } from 'styles/util';
import { getStorageSiteSubscriptionBookingPriceOverview } from 'actions/admin/storageSites';
import { handleResponse } from 'actions/actionHelpers';
import { getStorageGroupTitle } from 'helpers/StorageSiteHelper';
import routes from 'routes';

import Loader from 'common/Loader';
import AdminPage from '../../presentational/AdminPage';
import AdminContentWrapper from '../../presentational/AdminContentWrapper';
import { Link } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Amount from 'common/Amount';

const useStyles = makeStyles(({ theme }) => ({
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(4)
    },
    priceList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5)
    },
    priceListItem: {
        display: 'flex',
        gap: theme.spacing(2)
    },
    currentPrice: {
        fontWeight: 'bold',
    },
    price: {
        fontWeight: 'bold',
        flex: `0 0 ${theme.spacing(8)}px`
    },
    bookings: {
        flex: '1 1 auto',
        '& > *:not(:last-child)::after': {
            content: '", "'
        }
    }
}));

const StorageSiteSubscriptionBookingPriceOverview = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const params = useParams();
    const { appContext } = useAppContext();

    const [overview, setOverview] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);

    const { storageSiteId } = params;

    useEffect(() => {
        loadOverview();
    }, [storageSiteId]);

    const loadOverview = () => {
        setIsLoading(true);
        dispatch(getStorageSiteSubscriptionBookingPriceOverview(storageSiteId))
            .then(handleResponse(
                response => {
                    setOverview(response.payload);
                    setIsLoading(false);
                },
                () => setIsLoading(false)
            ));
    };

    const title = `Subscription booking price overview: ${overview?.storageSite.title ?? 'Loading...'}`;

    const actionPanel = (
        <Box>
            <Button
                type="button"
                variant="contained"
                color="primary"
                component={Link}
                to={routes.admin.storageSite.replace(':storageSiteId', storageSiteId)}
            >
                Back
            </Button>
        </Box>
    );

    return (
        <>
            <AdminPage title={title} actionPanel={actionPanel}>
                {
                    isLoading && <Loader />
                }
                {
                    !isLoading && overview &&
                    (
                        <AdminContentWrapper type="listing">
                            <Typography variant="body1" gutterBottom>
                                Prices per month, exclusive VAT. Only not yet started and active subscription bookings are listed.
                            </Typography>

                            <Box className={classes.list}>
                                {
                                    overview.storageGroupOverviews.map(storageGroupOverview => (
                                        <Box key={storageGroupOverview.storageGroup.id}>
                                            <Typography variant="h5" gutterBottom>{getStorageGroupTitle(storageGroupOverview.storageGroup, appContext, { forceShowSize: true })}</Typography>
                                            <Typography variant="body1" gutterBottom>
                                                Current price:
                                                {' '}
                                                <Box component="span" className={classes.currentPrice}><Amount value={storageGroupOverview.storageGroup.pricePerStorage} currency={storageGroupOverview.storageGroup.currency}/></Box>
                                            </Typography>
                                            {
                                                storageGroupOverview.prices.length === 0 && <Typography variant="body1">No bookings.</Typography>
                                            }
                                            {
                                                storageGroupOverview.prices.length > 0 &&
                                                (
                                                    <Box className={classes.priceList}>
                                                        {
                                                            storageGroupOverview.prices.map(price => (
                                                                <Box key={price.amount.value} className={classes.priceListItem}>
                                                                    <Box className={classes.price}>
                                                                        <Amount value={price.amount.value} currency={price.amount.currency}/>
                                                                    </Box>
                                                                    <Box className={classes.bookings}>
                                                                        {
                                                                            price.bookingIds.map(bookingId => (
                                                                                <Box key={bookingId} component="span">
                                                                                    <Link to={routes.admin.booking.replace(':bookingId', bookingId)}>{bookingId}</Link>
                                                                                </Box>
                                                                            ))
                                                                        }
                                                                    </Box>
                                                                </Box>
                                                            ))
                                                        }
                                                    </Box>
                                                )
                                            }
                                        </Box>
                                    ))
                                }
                            </Box>
                        </AdminContentWrapper>
                    )
                }
            </AdminPage>
        </>
    );
};

export default StorageSiteSubscriptionBookingPriceOverview;
